import { type Config, type Driver, type PopoverDOM } from 'driver.js';

import { useInvestorStore, investorOnboardingService } from '@tallo/investor/investor';

import { createTour, type TourStep } from './tour-driver';

export type TourType = 'property' | 'dashboard' | 'renterProfile';
type OnboardingFlag =
  | 'propertyOnboardingCompleted'
  | 'dashboardOnboardingCompleted'
  | 'renterProfileOnboardingCompleted';

export interface TourConfig extends Partial<Omit<Config, 'steps'>> {
  onboardingType: TourType;
  steps: TourStep[];
}

const completionFlags: Record<TourType, OnboardingFlag> = {
  property: 'propertyOnboardingCompleted',
  dashboard: 'dashboardOnboardingCompleted',
  renterProfile: 'renterProfileOnboardingCompleted',
};

const dashboardTabs = {
  showingRequests: 'pd-tab-showing-requests',
  escalations: 'pd-tab-escalations',
};

/** Selects a dashboard tab the way a click would, so the tour can show a pane the page only mounts on demand. */
function activateTab(tabId: string): void {
  const tab = document.getElementById(tabId);

  if (tab instanceof HTMLElement && tab.getAttribute('aria-selected') !== 'true') {
    tab.click();
  }
}

/**
 * Whether a tour may open is decided only by the investor's flag and by a tour already being on
 * screen. There is deliberately no "already shown this session" memory: the app switches accounts
 * without a reload, and such memory would carry over and block the next account's tours.
 */
class ProductTourService {
  private activeTours = new Map<TourType, Driver>();

  tryStartPropertyTour(propertyId: string) {
    if (!propertyId) return;

    if (this.canStart('property', 'propertyOnboardingCompleted')) {
      this.startPropertyTour();
    }
  }

  tryStartDashboardTour(propertyId: string) {
    if (!propertyId) return;

    if (this.canStart('dashboard', 'dashboardOnboardingCompleted')) {
      this.startDashboardTour();
    }
  }

  tryStartRenterProfileTour() {
    if (this.canStart('renterProfile', 'renterProfileOnboardingCompleted')) {
      this.startRenterProfileTour();
    }
  }

  /**
   * Discards a page's tour without reporting it completed, so it opens again the next time the page
   * shows. Pages call this on unmount so the overlay never outlives the page it was drawn over.
   * It takes the page's own tour type because unmount hooks run after the next page has mounted
   * and possibly started its tour; stopping "whatever is active" would kill that incoming tour.
   * The details page also mounts twice right after the add-property wizard (the wizard's store
   * reset lands after the new page has rendered the stale property), so a discarded tour must be
   * allowed to start over.
   */
  stopActiveTour(type: TourType): void {
    const tour = this.activeTours.get(type);
    if (!tour) return;

    this.activeTours.delete(type);
    tour.destroy();
  }

  private canStart(type: TourType, flag: OnboardingFlag): boolean {
    const investorStore = useInvestorStore();
    // `undefined` means the investor is not loaded yet; only an explicit false opens the tour
    if (investorStore.investor?.onboarding?.[flag] !== false) return false;

    // Asked of the driver rather than of our bookkeeping: driver.js reports a tour destroyed
    // before its first frame without calling onDestroyed, which would leave a stale entry here
    return !this.activeTours.get(type)?.isActive();
  }

  private startRenterProfileTour(): Driver {
    const config: TourConfig = {
      onboardingType: 'renterProfile',
      stagePadding: 6,
      steps: [
        {
          popover: {
            title: 'Hi there!',
            description:
              'Congrats on getting your first lead. Let me show you how to review it and take the next step.',
            showButtons: ['next', 'close'],
          },
        },
        {
          element: '#rp-showing-card',
          popover: {
            title: 'Requested time',
            description:
              "Here's the date and time they'd like to tour. If it works, Accept. If not, you can Reschedule or Decline.",
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#rp-highlights-card',
          popover: {
            title: 'Renter details',
            description: 'A quick snapshot of things like income, credit, and timeline — handy to decide fast.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#rp-inquiry-card',
          popover: {
            title: 'Application',
            description: "Ready to move forward? Send the application and I'll guide them through it.",
            side: 'top',
            align: 'end',
            doneBtnText: 'Finish',
          },
        },
      ],
    };

    return this.createTour(config);
  }

  /**
   * Gets default driver configuration that all tours share
   */
  getDefaultConfig(): Partial<Config> {
    return {
      showProgress: true,
      allowClose: true,
      overlayClickBehavior: 'nextStep',
      disableActiveInteraction: true,
      stagePadding: 14,
      stageRadius: 8,
      popoverClass: 't-tour',
      prevBtnText: 'Back',
      nextBtnText: 'Next',
      doneBtnText: 'Finish',
      onPopoverRender: (popover) => this.ensureAssistantHeader(popover),
    };
  }

  /**
   * Creates a completion handler for the given onboarding type
   */
  createCompletionHandler(onboardingType: TourConfig['onboardingType']) {
    let completionReported = false;

    return async () => {
      if (completionReported) return;
      completionReported = true;

      // Close the gate right away: the page can re-run its start check before the server round
      // trip below lands, and a failed round trip must not bring the tour straight back either
      const investorStore = useInvestorStore();
      investorStore.markOnboardingCompleted(completionFlags[onboardingType]);

      try {
        switch (onboardingType) {
          case 'property':
            await investorOnboardingService.completePropertyOnboarding();
            break;
          case 'dashboard':
            await investorOnboardingService.completeDashboardOnboarding();
            break;
          case 'renterProfile':
            await investorOnboardingService.completeRenterProfileOnboarding();
            break;
        }

        // Refresh investor data to get updated onboarding status
        await investorStore.load();
      } catch (e) {
        console.error(`Failed to complete ${onboardingType} onboarding`, e);
      }
    };
  }

  /**
   * Creates and starts a tour with the given configuration
   */
  createTour(config: TourConfig): Driver {
    const completionHandler = this.createCompletionHandler(config.onboardingType);

    // driver.js draws a single overlay for the page, so a tour still up from the page on its way
    // out has to go before this one opens
    for (const type of this.activeTours.keys()) this.stopActiveTour(type);

    const tour = createTour({
      ...this.getDefaultConfig(),
      ...config,
      onDestroyed: async (element, step, options) => {
        config.onDestroyed?.(element, step, options);

        // Only the user finishing or closing the tour counts, not a page discarding it
        if (this.activeTours.get(config.onboardingType) !== tour) return;
        this.activeTours.delete(config.onboardingType);
        await completionHandler();
      },
    });

    this.activeTours.set(config.onboardingType, tour);
    tour.drive();
    return tour;
  }

  private ensureAssistantHeader(popover: PopoverDOM) {
    const container = popover.wrapper;
    const titleEl = popover.title;
    if (!container || !titleEl) return;

    let header = container.querySelector('.t-tour-assistant') as HTMLElement | null;
    if (!header) {
      header = document.createElement('div');
      header.className = 't-tour-assistant';
      header.innerHTML =
        "<span class='avatar' aria-hidden='true' style='display:inline-flex;align-items:center;justify-content:center'>" +
        "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' style='color:#fe6723;display:inline-block'>" +
        "<path d='M4.5 6V1M2 3.5H7M13 3L11.2658 7.50886C10.9838 8.24209 10.8428 8.60871 10.6235 8.91709C10.4292 9.1904 10.1904 9.42919 9.91709 9.62353C9.60871 9.8428 9.24209 9.98381 8.50886 10.2658L4 12L8.50886 13.7342C9.24209 14.0162 9.60871 14.1572 9.91709 14.3765C10.1904 14.5708 10.4292 14.8096 10.6235 15.0829C10.8428 15.3913 10.9838 15.7579 11.2658 16.4911L13 21L14.7342 16.4911C15.0162 15.7579 15.1572 15.3913 15.3765 15.0829C15.5708 14.8096 15.8096 14.5708 16.0829 14.3765C16.3913 14.1572 16.7579 14.0162 17.4911 13.7342L22 12L17.4911 10.2658C16.7579 9.98381 16.3913 9.8428 16.0829 9.62353C15.8096 9.42919 15.5708 9.1904 15.3765 8.91709C15.1572 8.60871 15.0162 8.24209 14.7342 7.50886L13 3Z' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/>" +
        "</svg></span><span class='name'>Tallo Assistant</span>";
    }

    if (header.parentElement !== container || header.nextSibling !== titleEl) {
      container.insertBefore(header, titleEl);
    }
  }

  private startPropertyTour(): Driver {
    const config: TourConfig = {
      onboardingType: 'property',
      overlayOpacity: 0.5,
      steps: [
        {
          popover: {
            title: 'Congratulations!',
            description: `You’ve added your first property 🎉 Let me give you a quick tour so you know what's next.`,
            showButtons: ['next', 'close'],
          },
        },
        {
          element: '#prop-nav-details',
          popover: {
            title: 'Property details',
            description: `You’re on the property details page. When someone is interested in your property, I’ll use the info you gave me to answer their questions. If I can't answer, I’ll check with you.`,
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#prop-parking-card',
          popover: {
            title: 'Edit property details',
            description:
              'You can edit property info anytime — things like parking, pet policy, or renter requirements. I’ll update your listing everywhere automatically.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#prop-nav-availability',
          popover: {
            title: 'Availability',
            description:
              'This is where I learn your availability. I’ll use it to pre-book showings for you, but you can always step in and propose a different time.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#prop-list-btn',
          popover: {
            title: 'List property',
            description: `That’s it for the basics! When you’re ready, hit <strong>List Property</strong>, and I’ll kick off the process.`,
            // Below, not beside: for a target this close to the top of the viewport driver.js pins a
            // side popover's arrow to the popover's top corner, pointing well above the button
            side: 'bottom',
            align: 'end',
            doneBtnText: 'Finish',
          },
        },
      ],
    };

    return this.createTour(config);
  }

  private startDashboardTour(): Driver {
    const config: TourConfig = {
      onboardingType: 'dashboard',
      overlayOpacity: 0.8,
      // Closing the tour on the escalations step must not leave the dashboard on that tab
      onDestroyed: () => activateTab(dashboardTabs.showingRequests),
      steps: [
        {
          popover: {
            title: 'Nice work!',
            description: `Your listing is on its way to the rental sites, and I’ll let you know as soon as the first leads come in. In the meantime, let me show you around your property dashboard.`,
            showButtons: ['next', 'close'],
          },
        },
        {
          element: '#pd-performance',
          popover: {
            title: 'Track your performance',
            description:
              'Leads, messages, showings, and applications update in real time so you can see momentum at a glance. Market insights shows how your listing compares with nearby rentals.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#pd-auto-update',
          popover: {
            title: 'Smart updates',
            description: 'Enable auto‑updates and I’ll do my best to keep your listing top‑ranked.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#pd-listing-settings',
          popover: {
            title: 'Listing settings',
            description: 'Adjust tour options and screening sensitivity here. I’ll apply changes everywhere instantly.',
            side: 'top',
            align: 'end',
          },
        },
        {
          element: '#pd-engagement',
          prepare: () => activateTab(dashboardTabs.showingRequests),
          popover: {
            title: 'Showing requests',
            description: `As soon as I qualify a lead, I’ll hand them over to you here so you can decide whether to move forward.`,
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#pd-engagement',
          prepare: () => activateTab(dashboardTabs.escalations),
          popover: {
            title: 'Intelligent escalations',
            description:
              'If something needs your attention — like a property question, or renter running late — I’ll flag it here with a suggested next step.',
            side: 'top',
            align: 'start',
          },
        },
        {
          // The escalations tab stays selected here: switching it back would move this target
          // while the highlight travels to it. onDestroyed restores the default tab at the end.
          element: '#pd-upcoming-showings',
          popover: {
            title: 'Upcoming showings',
            description:
              'Here’s everything scheduled — easy to scan and manage. I’ll keep you posted if anything changes.',
            side: 'top',
            align: 'center',
            doneBtnText: 'Finish',
          },
        },
      ],
    };

    return this.createTour(config);
  }
}

export default new ProductTourService();
